// @ts-check
// ====== To-do list — data layer ======
// API client per la tabella `todos` + bucket Storage `todo-attachments`.
// Tutto user-scoped via RLS (vedi 20260528100000_todos.sql).

const TODO_BUCKET = 'todo-attachments';

/**
 * @typedef {Object} TodoAttachment
 * @property {string} path        Path nel bucket (es. "<uid>/<todo_id>/file.pdf")
 * @property {string} name        Nome originale del file
 * @property {number} size        Bytes
 * @property {string} mime        MIME type
 * @property {string} uploaded_at ISO timestamp
 */

/**
 * @typedef {Object} Todo
 * @property {string} id
 * @property {string} user_id
 * @property {string} titolo
 * @property {string|null} descrizione
 * @property {string|null} data_scadenza  YYYY-MM-DD
 * @property {boolean} completata
 * @property {string|null} completata_at
 * @property {TodoAttachment[]} allegati
 * @property {string} created_at
 * @property {string} updated_at
 */

function _safeName(name) {
  return String(name || 'file')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 120);
}

const todosApi = {
  /**
   * Lista tutti i to-do dell'utente loggato, ordinati per scadenza
   * (no scadenza in fondo) e poi per data di creazione discendente.
   * @returns {Promise<Todo[]>}
   */
  async list() {
    // Difesa in profondità: oltre alla RLS (che già limita le righe a auth.uid()),
    // filtriamo esplicitamente per utente, così il client richiede SOLO le proprie
    // attività. "Le mie attività" restano private al singolo utente loggato.
    // @ts-ignore — sb globale
    const { data: au } = await sb.auth.getUser();
    const uid = au?.user?.id;
    if (!uid) return [];
    // @ts-ignore — sb globale
    const { data, error } = await sb
      .from('todos')
      .select('*')
      .eq('user_id', uid)
      .order('completata', { ascending: true })
      .order('data_scadenza', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });
    if (error) throw error;
    return /** @type {Todo[]} */ (data || []);
  },

  /**
   * @param {{ titolo: string, descrizione?: string|null, data_scadenza?: string|null }} payload
   * @returns {Promise<Todo>}
   */
  async create(payload) {
    // @ts-ignore
    const { data: user } = await sb.auth.getUser();
    const uid = user?.user?.id;
    if (!uid) throw new Error('Utente non loggato');
    const row = {
      user_id: uid,
      titolo: (payload.titolo || '').trim(),
      descrizione: payload.descrizione || null,
      data_scadenza: payload.data_scadenza || null,
      allegati: []
    };
    if (!row.titolo) throw new Error('Il titolo è obbligatorio');
    // @ts-ignore
    const { data, error } = await sb.from('todos').insert(row).select('*').single();
    if (error) throw error;
    return /** @type {Todo} */ (data);
  },

  /**
   * @param {string} id
   * @param {Partial<Pick<Todo,'titolo'|'descrizione'|'data_scadenza'|'completata'>>} patch
   * @returns {Promise<Todo>}
   */
  async update(id, patch) {
    const allowed = {};
    if ('titolo' in patch) allowed.titolo = (patch.titolo || '').trim();
    if ('descrizione' in patch) allowed.descrizione = patch.descrizione || null;
    if ('data_scadenza' in patch) allowed.data_scadenza = patch.data_scadenza || null;
    if ('completata' in patch) allowed.completata = !!patch.completata;
    // @ts-ignore
    const { data, error } = await sb
      .from('todos')
      .update(allowed)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    return /** @type {Todo} */ (data);
  },

  /**
   * Toggle completata + traccia timestamp (gestito dal trigger DB).
   * @param {string} id
   * @param {boolean} completata
   * @returns {Promise<Todo>}
   */
  async toggleCompleted(id, completata) {
    return this.update(id, { completata });
  },

  /**
   * Cancella un to-do + tutti i suoi allegati nello Storage.
   * @param {Todo} todo
   */
  async remove(todo) {
    // 1. rimuovi i file dallo storage (se ci sono)
    const paths = (todo.allegati || []).map((a) => a.path).filter(Boolean);
    if (paths.length) {
      // @ts-ignore
      const { error } = await sb.storage.from(TODO_BUCKET).remove(paths);
      if (error) console.warn('Storage cleanup todo:', error);
    }
    // 2. rimuovi la riga
    // @ts-ignore
    const { error } = await sb.from('todos').delete().eq('id', todo.id);
    if (error) throw error;
  },

  /**
   * Carica un file come allegato. Path: <uid>/<todo_id>/<timestamp>-<safeName>
   * @param {string} todoId
   * @param {File} file
   * @returns {Promise<Todo>}
   */
  async addAttachment(todoId, file) {
    // @ts-ignore
    const { data: user } = await sb.auth.getUser();
    const uid = user?.user?.id;
    if (!uid) throw new Error('Utente non loggato');
    if (!file) throw new Error('Nessun file');
    if (file.size > 10 * 1024 * 1024) throw new Error('File troppo grande (max 10 MB)');

    const safe = _safeName(file.name);
    const ts = Date.now();
    const path = `${uid}/${todoId}/${ts}-${safe}`;

    // @ts-ignore
    const { error: upErr } = await sb.storage.from(TODO_BUCKET).upload(path, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: false
    });
    if (upErr) throw upErr;

    // Materializza metadati nel jsonb del todo
    // @ts-ignore
    const { data: todo, error: getErr } = await sb
      .from('todos')
      .select('allegati')
      .eq('id', todoId)
      .single();
    if (getErr) throw getErr;

    const next = Array.isArray(todo?.allegati) ? [...todo.allegati] : [];
    next.push({
      path,
      name: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
      uploaded_at: new Date().toISOString()
    });
    // @ts-ignore
    const { data: updated, error: updErr } = await sb
      .from('todos')
      .update({ allegati: next })
      .eq('id', todoId)
      .select('*')
      .single();
    if (updErr) throw updErr;
    return /** @type {Todo} */ (updated);
  },

  /**
   * Rimuove un allegato (file storage + entry jsonb).
   * @param {string} todoId
   * @param {string} path
   * @returns {Promise<Todo>}
   */
  async removeAttachment(todoId, path) {
    // @ts-ignore
    const { error: rmErr } = await sb.storage.from(TODO_BUCKET).remove([path]);
    if (rmErr) console.warn('Storage remove:', rmErr);

    // @ts-ignore
    const { data: todo, error: getErr } = await sb
      .from('todos')
      .select('allegati')
      .eq('id', todoId)
      .single();
    if (getErr) throw getErr;

    const next = (todo?.allegati || []).filter((a) => a.path !== path);
    // @ts-ignore
    const { data: updated, error: updErr } = await sb
      .from('todos')
      .update({ allegati: next })
      .eq('id', todoId)
      .select('*')
      .single();
    if (updErr) throw updErr;
    return /** @type {Todo} */ (updated);
  },

  /**
   * Genera signed URL temporanea (60 minuti) per scaricare un allegato.
   * @param {string} path
   * @returns {Promise<string|null>}
   */
  async signedUrl(path) {
    // @ts-ignore
    const { data, error } = await sb.storage.from(TODO_BUCKET).createSignedUrl(path, 3600);
    if (error) {
      console.warn('signedUrl:', error);
      return null;
    }
    return data?.signedUrl || null;
  }
};

// @ts-ignore — registra global per le UI
globalThis.todosApi = todosApi;
export {};
